import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import businessLogo from "../assets/business-logo.png";
import exteriorSummaryImage from "../assets/exterior.png";
import interiorSummaryImage from "../assets/interior.png";
import dashboardSectionImg from "../assets/Interior mobile pics/DASHBOARD.jpg";
import rightDoorsImage from "../assets/Interior mobile pics/right doors.jpg";
import leftDoorsImage from "../assets/Interior mobile pics/left doors.jpg";
import carpetsAndBootImage from "../assets/Interior mobile pics/carpets and boot.jpg";
import roofImage from "../assets/Interior mobile pics/roof.jpg";
import tailgatePadImage from "../assets/Interior mobile pics/tailgate pad.jpg";
import interiorSeatingImage from "../assets/Interior mobile pics/interior seating.jpg";
import { aiSummarizeAppraisal } from "../services/ai";
import { API_BASE } from "../services/api";

// Mobile Exterior Section Images
import frontBumperImg from "../assets/exterior mob pic/front bumper.jpg";
import rearBumperImg from "../assets/exterior mob pic/rare bumper.jpg";
import driverSideImg from "../assets/exterior mob pic/driver side 2.png";
import passengerSideImg from "../assets/exterior mob pic/passenger 2.png";
import roofExteriorImg from "../assets/exterior mob pic/roof view.png";

import iconEngine from "../assets/icons/engine.png";
import iconFuel from "../assets/icons/fuel.png";
import iconHybrid from "../assets/icons/electric.png";
import iconTransmission from "../assets/icons/gear.png";
import iconMot from "../assets/icons/mot.png";
import iconTax from "../assets/icons/tax.png";
import iconColor from "../assets/icons/color.png";
import iconEu from "../assets/icons/eu.png";
import iconWeight from "../assets/icons/weight.png";
import iconDocument from "../assets/icons/document.png";
import iconYear from "../assets/icons/calendar.png";

import iconUk from "../assets/icons/uk.png";
import iconSpeedometer from "../assets/icons/speedometer.png";
import iconUsers from "../assets/icons/users.png";
import iconKey from "../assets/icons/car-key.png";
import iconShield from "../assets/icons/shield.png";
import iconMaintenance from "../assets/icons/maintenance.png";
import iconRoad from "../assets/icons/road.png";

const toDataURL = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
    });
};

const imgFormatFromDataUrl = (dataUrl) =>
    dataUrl && dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";

/* ----------------- FORMAT HELPERS ----------------- */

const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
        const str = String(dateStr).trim();
        if (str.includes("T")) {
            const d = new Date(str);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString("en-GB");
            }
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
            const [y, m, d] = str.split("-");
            return `${d}/${m}/${y}`;
        }
        if (/^\d{4}-\d{2}$/.test(str)) {
            const [y, m] = str.split("-");
            return `${m}/${y}`;
        }
        return str;
    } catch {
        return String(dateStr);
    }
};

const formatFuelType = (fuel) => {
    if (!fuel) return "";
    const map = {
        "HEAVY OIL": "Diesel",
        "DIESEL": "Diesel",
        "PETROL": "Petrol",
        "ELECTRICITY": "Electric",
        "ELECTRIC": "Electric",
        "HYBRID ELECTRIC": "Hybrid",
        "PLUG-IN HYBRID": "Plug-in Hybrid",
        "GAS": "Gas (LPG)",
        "GAS BI-FUEL": "Gas Bi-Fuel",
        "STEAM": "Steam",
        "FUEL CELLS": "Hydrogen"
    };
    const upper = String(fuel).toUpperCase().trim();
    return map[upper] || fuel;
};

/* ----------------- SECTION BAR & FOOTER ----------------- */

const drawSectionBar = (doc, title, y, [r, g, b]) => {
    doc.setFillColor(r, g, b);
    doc.rect(5, y, 200, 12, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text(title, 10, y + 8);
    doc.setTextColor(0);
};

const addFooterAllPages = (doc, data) => {
    const pageCount = doc.internal.getNumberOfPages();
    const footerMid = `${data.reg || ""}, ${data.make || ""} ${data.model || ""}`.trim();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const W = doc.internal.pageSize.getWidth();
        const H = doc.internal.pageSize.getHeight();
        doc.setDrawColor(220);
        doc.line(5, H - 12, W - 5, H - 12);
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(`Page ${i}`, 5, H - 6);
        doc.text(footerMid, W / 2, H - 6, { align: "center" });
        doc.text("Heston Automotive", W - 5, H - 6, { align: "right" });
    }
};

/* ----------------- LABEL ICON HELPERS ----------------- */

const iconImages = {
    "colour": iconColor,
    "fuel": iconFuel,
    "engine": iconEngine,
    "transmission": iconTransmission,
    "euro": iconEu,
    "mot": iconMot,
    "tax": iconTax,
    "revenue": iconWeight,
    "year": iconYear,
    "v5": iconDocument,
    "hybrid": iconHybrid,
    "electric": iconHybrid,
    "uk": iconUk,
    "mileage": iconSpeedometer,
    "owner": iconUsers,
    "key": iconKey,
    "insurance": iconShield,
    "write-off": iconShield,
    "service": iconMaintenance,
    "usage": iconRoad,
    "history": iconRoad,
    // Mechanical checks icons
    "oil": iconMaintenance,           // Oil Leakage
    "coolant": iconEngine,            // Coolant Level
    "brake": iconShield,              // Brake Fluid Level
    "warning": iconMot,               // Warning Lights/Messages
    "battery": iconEngine,            // Battery Condition
    "exhaust": iconMaintenance        // Exhaust Condition
};

const preloadIcons = async () => {
    const loaded = {};
    for (const [key, src] of Object.entries(iconImages)) {
        try {
            loaded[key] = await toDataURL(src);
        } catch (e) {
            console.error(`Failed to load icon: ${key}`, e);
        }
    }
    return loaded;
};

const getIconFromCache = (label, iconCache) => {
    const l = (label || "").toLowerCase();
    const key = Object.keys(iconCache).find(k => l.includes(k));
    return key ? iconCache[key] : null;
};

/* ----------------- PINS & FAULT LIST ----------------- */

const drawPin = (
    doc, xPx, yPx, number, imgX, imgY, imgW, imgH, originalW, originalH, fromPrevious = false
) => {
    const scaleX = imgW / originalW;
    const scaleY = imgH / originalH;
    const px = imgX + xPx * scaleX;
    const py = imgY + yPx * scaleY;
    const outerR = 2.0;
    const innerR = 0.8;

    if (fromPrevious) {
        doc.setFillColor(249, 115, 22);
    } else {
        doc.setFillColor(17, 17, 17);
    }

    doc.circle(px, py, innerR, "F");
    doc.circle(px, py, outerR, "F");
    doc.setFontSize(4.5);
    doc.setTextColor(255, 255, 255);
    doc.text(String(number), px, py + 1.0, { align: "center" });
    doc.setTextColor(0);
};

const renderFaultList = (doc, items, startX, startY, startNum = 1) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);

    const clean = (s) => (s || "").trim();
    const data = (items || [])
        .map((it) => typeof it === "string" ? { text: clean(it), idx: null } : { text: clean(it.text), idx: it.idx ?? null })
        .filter((it) => it.text)
        .map((it) => ({ ...it, text: it.text.replace(/\s+/g, " ") }))
        .slice(0, 999);

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const rightMaxW = pageW - startX - 5;
    const bottom = pageH - 20;

    let y = startY;

    data.forEach((item, i) => {
        const n = item.idx != null ? item.idx : startNum + i;
        const label = `${n}) `;
        doc.text(label, startX, y);

        const labelWidth = doc.getTextWidth(label);
        const textStartX = startX + labelWidth;
        const wrapWidth = Math.max(10, rightMaxW - labelWidth);
        const wrapped = doc.splitTextToSize(item.text, wrapWidth);

        wrapped.forEach((line, lineIdx) => {
            const x = lineIdx === 0 ? textStartX : startX;
            doc.text(line, x, y);
            y += 5;

            if (y > bottom) {
                doc.addPage();
                doc.setFont("helvetica", "normal");
                doc.setFontSize(11);
                doc.setTextColor(0, 0, 0);
                y = 20;
            }
        });
        y += 2;
    });
};

const drawPhotoPinExact = (doc, x, y, n, { R = 6, border = 1.1, fill = [230, 118, 33], tailLen = 5, baseK = 0.7, baseW = 0.75 } = {}) => {
    const [r, g, b] = fill;

    const R_out = R;
    const baseY1 = y + R_out * baseK;
    const leftX1 = x - R_out * baseW;
    const rightX1 = x + R_out * baseW;
    const tipY1 = y + R_out + tailLen;

    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(255, 255, 255);
    doc.circle(x, y, R_out, "F");
    doc.triangle(leftX1, baseY1, rightX1, baseY1, x, tipY1, "F");

    const R_in = Math.max(1, R_out - border);
    const baseY2 = y + R_in * baseK;
    const leftX2 = x - R_in * baseW;
    const rightX2 = x + R_in * baseW;
    const tipY2 = tipY1 - border;

    doc.setFillColor(r, g, b);
    doc.setDrawColor(r, g, b);
    doc.circle(x, y, R_in, "F");
    doc.triangle(leftX2, baseY2, rightX2, baseY2, x, tipY2, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(R_in * 2.3);
    doc.text(String(n), x, y + 1, { align: "center" });

    doc.setTextColor(0, 0, 0);
};

/* ----------------- MAIN VEHICLE PDF ----------------- */

export const generatePDF = async (
    data,
    { mode = "download", exteriorSummaryImageBase64, interiorSummaryImageBase64 } = {}
) => {
    const doc = new jsPDF();

    const iconCache = await preloadIcons();

    let extBase64 = exteriorSummaryImageBase64;
    if (!extBase64) {
        try {
            extBase64 = await toDataURL(exteriorSummaryImage);
        } catch (e) {
            console.error("Failed to load default exterior image", e);
        }
    }

    let intBase64 = interiorSummaryImageBase64;
    if (!intBase64) {
        try {
            intBase64 = await toDataURL(interiorSummaryImage);
        } catch (e) {
            console.error("Failed to load default interior image", e);
        }
    }

    // ============ POLISHED HEADER SECTION ============
    const pageW = doc.internal.pageSize.getWidth();
    const headerCenterY = 15;

    // 1. Logo (Top Left)
    try {
        const logoDataUrl = await toDataURL(data?.logoUrl || businessLogo);
        const logoW = 38;
        const logoH = 11;
        const logoX = 8;
        const logoY = headerCenterY - (logoH / 2);
        doc.addImage(logoDataUrl, "PNG", logoX, logoY, logoW, logoH);
    } catch (e) {
        console.error("Logo load failed:", e);
    }

    // 2. Main Title (Center)
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(0, 51, 102);
    doc.text("Vehicle Appraisal Report", pageW / 2 + 5, headerCenterY + 3, { align: "center" });

    // 3. Grade Circle (Top Right)
    if (data?.overallGrade) {
        const grade = String(data.overallGrade).toUpperCase();
        let gradeColor;

        switch (grade) {
            case "1": gradeColor = [76, 175, 80]; break;
            case "2": gradeColor = [139, 195, 74]; break;
            case "3": gradeColor = [255, 193, 7]; break;
            case "4": gradeColor = [255, 152, 0]; break;
            case "5": gradeColor = [183, 28, 28]; break;
            case "U": gradeColor = [158, 158, 158]; break;
            default: gradeColor = [73, 180, 85];
        }

        const gradeX = 192;
        const gradeR = 9;

        doc.setFillColor(...gradeColor);
        doc.circle(gradeX, headerCenterY, gradeR, "F");

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.text("Grade", gradeX, headerCenterY - 2, { align: "center" });

        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text(String(data.overallGrade), gradeX, headerCenterY + 5, { align: "center" });
    }

    // Divider line
    doc.setDrawColor(200);
    doc.setLineWidth(0.4);
    doc.line(5, 28, 205, 28);

    // ============ VEHICLE INFO ROW ============
    const rowTopY = 32;

    // --- A. Registration Plate ---
    const regText = data.reg || "N/A";
    const plateWidth = 38;
    const plateHeight = 10;
    const plateX = 8;
    const plateY = rowTopY;

    doc.setFillColor(255, 210, 0);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.3);
    doc.roundedRect(plateX, plateY, plateWidth, plateHeight, 1.5, 1.5, "FD");

    const blueStripW = 7;
    doc.setFillColor(0, 51, 153);
    doc.roundedRect(plateX, plateY, blueStripW, plateHeight, 1.5, 1.5, "F");
    doc.rect(plateX + 3, plateY, blueStripW - 3, plateHeight, "F");

    const ukIcon = iconCache["uk"];
    if (ukIcon) {
        const flagW = 4.5;
        const flagH = 3;
        doc.addImage(ukIcon, "PNG", plateX + (blueStripW - flagW) / 2, plateY + 1.5, flagW, flagH);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(5.5);
    doc.setTextColor(255, 255, 255);
    doc.text("UK", plateX + blueStripW / 2, plateY + plateHeight - 1.5, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    const textCenterX = plateX + blueStripW + (plateWidth - blueStripW) / 2;
    doc.text(regText, textCenterX, plateY + 6.5, { align: "center" });

    // --- B. Vehicle Description ---
    const descX = 52;
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);

    const makeModel = `${data.make || ""} ${data.model || ""}`.trim();
    const maxDescWidth = 92;
    const titleLines = doc.splitTextToSize(makeModel, maxDescWidth);
    doc.text(titleLines, descX, rowTopY + 5);

    // Track the last Y position of content for dynamic positioning
    let lastContentY = rowTopY + 5 + (titleLines.length * 5);

    // Mileage display
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.setFont("helvetica", "normal");
    doc.text(`Mileage: ${data.mileage || "N/A"}`, descX, lastContentY);

    // VIN display - show if available
    const vinValue = data.vin || data.vehicleIdentificationNumber || '';
    console.log('🔍 PDF VIN Debug:', {
        vin: data.vin,
        vehicleIdentificationNumber: data.vehicleIdentificationNumber,
        vinValue: vinValue,
        trimmed: String(vinValue).trim(),
        allData: data
    });

    if (vinValue && String(vinValue).trim() && String(vinValue).trim().toUpperCase() !== 'N/A') {
        lastContentY += 4;  // Move down for VIN
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.setFont("helvetica", "normal");
        doc.text(`VIN: ${vinValue}`, descX, lastContentY);
        console.log('✅ VIN displayed in PDF:', vinValue);
    } else {
        console.log('❌ VIN not displayed - failed condition check');
    }


    // --- C. Appraised Info Box ---
    const infoBoxX = 148;
    const infoBoxY = rowTopY;
    const infoBoxW = 54;
    const infoBoxH = 14;

    doc.setFillColor(248, 249, 250);
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.roundedRect(infoBoxX, infoBoxY, infoBoxW, infoBoxH, 2, 2, "FD");

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text("Appraised:", infoBoxX + 3, infoBoxY + 5);
    doc.text("By:", infoBoxX + 3, infoBoxY + 11);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(formatDate(data.date) || "N/A", infoBoxX + 24, infoBoxY + 5);
    doc.text(data.submittedBy || "Staff", infoBoxX + 24, infoBoxY + 11);

    // --- D. Divider Line (DYNAMIC: based on content height) ---
    const dividerY = Math.max(lastContentY + 4, infoBoxY + infoBoxH + 2);  // Whichever is lower
    doc.setDrawColor(200);
    doc.setLineWidth(0.3);
    doc.line(5, dividerY, 205, dividerY);

    let currentY = dividerY + 4;  // Table starts after divider


    /* ------------ Vehicle details table ------------ */

    const potentialFields = [
        { label: "Colour", value: data.colour },
        { label: "Fuel Type", value: formatFuelType(data.fuelType) },
        { label: "Engine Size (cc)", value: data.engineSize },
        { label: "Transmission", value: data.transmission || "N/A" },
        { label: "CO2 Emissions", value: data.co2 },
        { label: "Euro Status", value: data.euroStatus },
        { label: "Reg Date", value: formatDate(data.regDate) },
        { label: "Year (derived)", value: data.year },
        { label: "Year of Manufacture", value: data.yearOfManufacture },
        { label: "V5C Last Issued", value: formatDate(data.dateOfLastV5CIssued) },
        { label: "MOT Status", value: data.motStatus },
        { label: "MOT Expiry", value: formatDate(data.motExpiryDate) },
        { label: "Tax Status", value: data.taxStatus },
        { label: "Tax Due Date", value: formatDate(data.taxDueDate) },
        { label: "Revenue Weight", value: data.revenueWeight ? `${data.revenueWeight} kg` : "" }
    ];

    const validFields = potentialFields.filter(
        (f) => f.value && String(f.value).trim() !== "" && String(f.value).trim() !== "N/A"
    );

    const vehicleTableBody = [];
    for (let i = 0; i < validFields.length; i += 2) {
        const left = validFields[i];
        const right = validFields[i + 1];
        vehicleTableBody.push([
            left.label,
            left.value,
            right ? right.label : "",
            right ? right.value : ""
        ]);
    }

    autoTable(doc, {
        startY: currentY,
        head: [["Field", "Value", "Field", "Value"]],
        body: vehicleTableBody,
        styles: { fontSize: 10, cellPadding: 2.5 },
        headStyles: { fillColor: [0, 51, 102], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        margin: { left: 5, right: 5 },
        didParseCell: (hookData) => {
            if (hookData.section === "body" && (hookData.column.index === 0 || hookData.column.index === 2)) {
                let pad = hookData.cell.styles.cellPadding;
                if (typeof pad === "number") {
                    hookData.cell.styles.cellPadding = { top: pad, right: pad, bottom: pad, left: pad + 6 };
                } else {
                    hookData.cell.styles.cellPadding = { ...pad, left: (pad.left || 2) + 6 };
                }
            }
        },
        didDrawCell: (hookData) => {
            if (hookData.section === "body" && (hookData.column.index === 0 || hookData.column.index === 2)) {
                const label = String(hookData.cell.raw || "");
                const icon = getIconFromCache(label, iconCache);

                if (icon) {
                    const x = hookData.cell.x + 2;
                    const y = hookData.cell.y + 2;
                    doc.addImage(icon, "PNG", x, y, 5, 5);
                }
            }
        }
    });

    currentY = doc.lastAutoTable.finalY + 10;

    /* ------------ Exterior condition ------------ */

    await (async () => {
        const pageH = doc.internal.pageSize.getHeight();
        const minNeeded = 12 + 6 + 70 + 16;
        let yBar = currentY;

        if (yBar + minNeeded > pageH - 20) {
            doc.addPage();
            yBar = 20;
        }

        drawSectionBar(doc, "Exterior condition", yBar, [0, 172, 193]);
        doc.setDrawColor(220);
        doc.setLineWidth(0.5);
        doc.line(5, yBar + 13, 205, yBar + 13);



        const clean = (s) => (s || "").trim();
        const extList = (data.faults?.exterior || []).map((f) => {
            const baseText = `${f.part || ""}; ${f.damage || ""}; ${f.detail || ""}`;
            const noteText = f.note ? ` - Note: ${f.note}` : "";
            return {
                idx: f.idx,
                text: clean(baseText + noteText).trim()
            };
        });

        // DETECT MOBILE EXTERIOR FAULTS
        const hasMobileExtFaults = (data.faults?.exterior || []).some(f => f.sectionId);
        const isMobileExtAppraisal = data.deviceType === 'mobile' || hasMobileExtFaults;

        if (isMobileExtAppraisal) {
            // --- MOBILE LAYOUT FOR EXTERIOR ---
            let currentExtY = yBar + 18;
            const exteriorSections = [
                { id: 'ext-front', title: 'Front Bumper', img: frontBumperImg },
                { id: 'ext-rear', title: 'Rear Bumper', img: rearBumperImg },
                { id: 'ext-left', title: 'Passenger Side (Left)', img: passengerSideImg },
                { id: 'ext-right', title: 'Driver Side (Right)', img: driverSideImg },
                { id: 'ext-roof', title: 'Roof, Glass & Top', img: roofExteriorImg }
            ];

            let faultsFound = false;

            for (const section of exteriorSections) {
                const sectionFaults = (data.faults?.exterior || []).filter(f => f.sectionId === section.id);

                if (sectionFaults.length > 0) {
                    faultsFound = true;
                    // Check page break
                    if (currentExtY > pageH - 80) {
                        doc.addPage();
                        currentExtY = 20;
                    }

                    // Section Title
                    doc.setFontSize(10);
                    doc.setFont("helvetica", "bold");
                    doc.text(section.title, 5, currentExtY - 2);

                    const base64Img = await toDataURL(section.img);
                    let imgH = 40; // Reduced height
                    if (base64Img) {
                        const imgW = 60; // Reduced width
                        // Just use fixed aspect or load dimensions if critical. 
                        // For simplicity using fixed width approx ratio.
                        doc.addImage(base64Img, "JPEG", 5, currentExtY, imgW, imgH);

                        // Draw pins
                        sectionFaults.forEach((f) => {
                            if (f?.coords && f?.idx) {
                                const pinX = 5 + (f.coords.x / 100) * imgW;
                                const pinY = currentExtY + (f.coords.y / 100) * imgH;

                                doc.setFillColor(17, 17, 17); // Use black for exterior pins or match generic logic
                                doc.circle(pinX, pinY, 3, 'F');
                                doc.setTextColor(255, 255, 255);
                                doc.setFontSize(7);
                                doc.text(String(f.idx), pinX, pinY + 1, { align: 'center' });
                            }
                        });
                    }

                    // List faults on the right
                    const listX = 100;
                    const sectionList = sectionFaults.map((f) => {
                        const baseText = `${f.part || ""}; ${f.damage || ""}; ${f.detail || ""}`;
                        const noteText = f.note ? ` - Note: ${f.note}` : "";
                        return { idx: f.idx, text: clean(baseText + noteText).trim() };
                    });

                    renderFaultList(doc, sectionList, listX, currentExtY, 0);

                    currentExtY += imgH + 15;
                }
            }

            if (!faultsFound) {
                doc.setFontSize(11);
                doc.setTextColor(0);
                doc.text("No exterior faults recorded.", 10, currentExtY);
                currentExtY += 10;
            }

            currentY = currentExtY; // Update main currentY

        } else {
            // --- DESKTOP LAYOUT (Original) ---
            const imgX = 5, imgY = yBar + 18, imgW = 100, imgH = 70;
            try {
                if (typeof extBase64 === "string" && extBase64) {
                    doc.addImage(extBase64, "PNG", imgX, imgY, imgW, imgH);
                }

                const originalW = 1332, originalH = 733;
                (data.faults?.exterior || []).forEach((f) => {
                    if (f?.coords && f?.idx) {
                        drawPin(doc, f.coords.x, f.coords.y, f.idx, imgX, imgY, imgW, imgH, originalW, originalH, f.fromPrevious);
                    }
                });
            } catch (e) {
                console.error("Exterior section error:", e);
            }
            renderFaultList(doc, extList, 110, imgY + 5, 1);
            currentY = imgY + imgH + 12;
        }
    })();

    /* ------------ Interior condition ------------ */

    doc.addPage();
    drawSectionBar(doc, "Interior condition", 18, [0, 172, 193]);
    doc.setDrawColor(220);
    doc.setLineWidth(0.5);
    doc.line(5, 31, 205, 31);

    try {
        // Check if mobile appraisal - either by deviceType or by presence of sectionId in faults
        const hasMobileFaults = (data.faults?.interior || []).some(f => f.sectionId);
        const isMobileAppraisal = data.deviceType === 'mobile' || hasMobileFaults;
        console.log('🖨️ PDF Interior - deviceType:', data.deviceType, 'hasMobileFaults:', hasMobileFaults, 'isMobile:', isMobileAppraisal);

        if (isMobileAppraisal) {
            // MOBILE: Show section-based images with markers

            let currentY = 36;
            const sections = [
                { id: 'dashboard', title: 'Dashboard Area', img: dashboardSectionImg },
                { id: 'driver-door', title: 'Right Side Front & Back Door', img: rightDoorsImage },
                { id: 'passenger-door', title: 'Left Side Front & Back Door', img: leftDoorsImage },
                { id: 'carpet', title: 'Carpet and Boot Area', img: carpetsAndBootImage },
                { id: 'roof', title: 'Roof Lining', img: roofImage },
                { id: 'tailgate-pad', title: 'Tailgate Pad', img: tailgatePadImage },
                { id: 'interior-seating', title: 'Interior Seating Area', img: interiorSeatingImage }
            ];

            for (const section of sections) {
                const sectionFaults = (data.faults?.interior || []).filter(f => f.sectionId === section.id);

                if (sectionFaults.length > 0) {
                    // Check if we need a new page
                    if (currentY > 200) {
                        doc.addPage();
                        currentY = 20;
                    }

                    // Section Title
                    doc.setFontSize(10);
                    doc.setFont("helvetica", "bold");
                    doc.text(section.title, 5, currentY - 2);

                    const base64Img = await toDataURL(section.img);
                    if (base64Img) {
                        const imgProps = await new Promise((resolve) => {
                            const img = new Image();
                            img.onload = () => resolve({ width: img.width, height: img.height });
                            img.onerror = () => resolve({ width: 1000, height: 700 });
                            img.src = base64Img;
                        });

                        const imgW = 60; // Reduced width (was 90)
                        const imgH = 40; // Fixed height (was ratio based)

                        doc.addImage(base64Img, "JPEG", 5, currentY, imgW, imgH);

                        // Draw pins
                        sectionFaults.forEach((f) => {
                            if (f?.coords && f?.idx) {
                                const pinX = 5 + (f.coords.x / 100) * imgW;
                                const pinY = currentY + (f.coords.y / 100) * imgH;

                                doc.setFillColor(230, 118, 33);
                                doc.circle(pinX, pinY, 3, 'F');
                                doc.setTextColor(255, 255, 255);
                                doc.setFontSize(7);
                                doc.text(String(f.idx), pinX, pinY + 1, { align: 'center' });
                            }
                        });

                        // Fault list for this section on the right side
                        const listX = 100;
                        const listY = currentY;

                        const faultList = sectionFaults.map((f) => {
                            const baseText = `${f.part || ""}; ${f.damage || ""}; ${f.detail || ""}`;
                            const noteText = f.note ? ` - Note: ${f.note}` : "";
                            return { idx: f.idx, text: (baseText + noteText).trim() };
                        });

                        renderFaultList(doc, faultList, listX, listY + 5, 0); // extCount passed as 0 as we use simple list here? Or need logic.
                        // Actually renderFaultList adds indices. Let's precise that.

                        currentY += Math.max(imgH, faultList.length * 10) + 15;
                    }
                }
            }

            if (currentY === 36) {
                doc.setFontSize(11);
                doc.text("No interior faults recorded on mobile", 10, 45);
            }
        } else {
            // DESKTOP: Original single interior image
            const imgW = 100, imgH = 70, imgX = 5, imgY = 36;
            if (typeof intBase64 === "string" && intBase64) {
                doc.addImage(intBase64, "PNG", imgX, imgY, imgW, imgH);
            }

            const originalW = 1153, originalH = 718;
            (data.faults?.interior || []).forEach((f) => {
                if (f?.coords && f?.idx) {
                    drawPin(doc, f.coords.x, f.coords.y, f.idx, imgX, imgY, imgW, imgH, originalW, originalH, f.fromPrevious);
                }
            });

            const intList = (data.faults?.interior || []).map((f) => {
                const baseText = `${f.part || ""}; ${f.damage || ""}; ${f.detail || ""}`;
                const noteText = f.note ? ` - Note: ${f.note}` : "";
                return {
                    idx: f.idx,
                    text: (baseText + noteText).trim()
                };
            });
            const extCount = (data.faults?.exterior || []).length;
            renderFaultList(doc, intList, 110, 41, extCount + 1);
        }
    } catch (e) {
        console.error("Interior section error:", e);
    }

    /* ------------ Fault photos ------------ */

    const photos = [
        ...(data.faults?.exterior || []),
        ...(data.faults?.interior || [])
    ].filter((f) => f.photo || f.photoUrl);

    if (photos.length > 0) {
        doc.addPage();
        drawSectionBar(doc, "Fault Photos", 18, [0, 172, 193]);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);

        const cols = 2;
        const photoW = 90;
        const photoH = 68;
        const gapX = 10;
        const gapY = 28;
        const startX = 10;
        const startY = 35;

        let x = startX;
        let y = startY;
        let col = 0;

        for (let i = 0; i < photos.length; i++) {
            const f = photos[i];
            try {
                const raw = f.photo || f.photoUrl;
                const isUrl = typeof raw === "string" && (/^https?:\/\//i.test(raw) || raw.startsWith("/"));
                const absUrl = isUrl && raw.startsWith("/") ? `${API_BASE}${raw}` : raw;
                const src = isUrl ? await toDataURL(absUrl) : raw;

                if (y + photoH > doc.internal.pageSize.getHeight() - 25) {
                    doc.addPage();
                    drawSectionBar(doc, "Fault Photos (cont.)", 18, [0, 172, 193]);
                    doc.setFont("helvetica", "normal");
                    doc.setFontSize(10);
                    x = startX;
                    y = startY;
                    col = 0;
                }

                doc.addImage(src, imgFormatFromDataUrl(src), x, y, photoW, photoH);
                drawPhotoPinExact(doc, x + 6, y + 5, f.idx || i + 1, {
                    R: 2.5,
                    border: 0.3,
                    fill: [230, 118, 33],
                    tailLen: 1.0
                });

                doc.setFontSize(10);
                const caption = `${f.part || "Unknown"} — ${f.damage || ""}${f.detail ? " — " + f.detail : ""}${f.note ? " — " + f.note : ""}`;
                doc.text(
                    caption,
                    x,
                    y + photoH + 5,
                    { maxWidth: photoW }
                );

                if (isUrl && absUrl) {
                    doc.link(x, y, photoW, photoH, { url: absUrl, target: "_blank" });
                }

                col++;
                if (col < cols) {
                    x += photoW + gapX;
                } else {
                    col = 0;
                    x = startX;
                    y += photoH + gapY;
                }
            } catch (err) {
                console.error("Error adding photo:", err);
            }
        }
    }

    /* ------------ AI Summary ------------ */

    let summaryText = data.aiSummary;

    if (!summaryText) {
        try {
            summaryText = await aiSummarizeAppraisal({
                ...data,
                faults: data.faults || { exterior: [], interior: [] },
                overallGrade: data.overallGrade || ""
            });
        } catch (e) {
            console.error("AI summary (on-demand) failed:", e);
            summaryText = "AI summary unavailable.";
        }
        data.aiSummary = summaryText;
    }

    doc.addPage();
    drawSectionBar(doc, "Appraisal Summary", 18, [0, 172, 193]);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);

    const wrapped = doc.splitTextToSize(summaryText || "AI summary unavailable.", 190);
    const summaryHeight = wrapped.length * 6;

    doc.text(wrapped, 10, 36);

    currentY = 36 + summaryHeight + 10;

    /* ------------ Vehicle History ------------ */

    const conditionFields = [
        { label: "Mileage", value: data.vehicleMileage },
        { label: "Mileage Modification", value: data.mileageModifications },
        { label: "V5 Document Present", value: data.hasV5Document },
        { label: "Number of Owners", value: data.numberOfOwners },
        { label: "Number of Keys", value: data.numberOfKeys },
        { label: "First Registered in UK", value: data.firstRegisteredUK },
        { label: "Insurance Write-Off", value: data.insuranceWriteOff },
        ...(data.insuranceWriteOff === "yes" ? [{ label: "Write-Off Category", value: data.insuranceWriteOffType }] : []),
        { label: "Service History", value: data.serviceHistoryType },
        { label: "Service Records Count", value: data.serviceHistoryCount },
        { label: "Main Dealer Serviced", value: data.mainDealer },
        { label: "Usage History", value: data.vehicleUsageHistory },
        { label: "MOT Status", value: data.motStatus },
        { label: "MOT Expiry Date", value: formatDate(data.motExpiryDate) },
        ...(data.additionalNotes ? [{ label: "Additional Notes", value: data.additionalNotes }] : [])
    ];

    const validCondition = conditionFields.filter(
        (f) => f.value && String(f.value).trim() !== "" && String(f.value).trim() !== "N/A"
    );

    if (validCondition.length > 0) {
        const pageHeight = doc.internal.pageSize.getHeight();
        if (currentY + 60 > pageHeight - 20) {
            doc.addPage();
            currentY = 25;
        }

        drawSectionBar(doc, "Vehicle History", currentY, [0, 172, 193]);
        currentY += 15;

        const conditionBody = [];
        for (let i = 0; i < validCondition.length; i += 2) {
            const left = validCondition[i];
            const right = validCondition[i + 1];
            conditionBody.push([
                left.label,
                left.value,
                right ? right.label : "",
                right ? right.value : ""
            ]);
        }

        autoTable(doc, {
            startY: currentY,
            body: conditionBody,
            styles: { fontSize: 10, cellPadding: 2.5 },
            alternateRowStyles: { fillColor: [249, 250, 251] },
            margin: { left: 5, right: 5 },
            didParseCell: (hookData) => {
                if (hookData.section === "body" && (hookData.column.index === 0 || hookData.column.index === 2)) {
                    let pad = hookData.cell.styles.cellPadding;
                    if (typeof pad === "number") {
                        hookData.cell.styles.cellPadding = { top: pad, right: pad, bottom: pad, left: pad + 6 };
                    } else {
                        hookData.cell.styles.cellPadding = { ...pad, left: (pad.left || 2) + 6 };
                    }
                }
            },
            didDrawCell: (hookData) => {
                if (hookData.section === "body" && (hookData.column.index === 0 || hookData.column.index === 2)) {
                    const label = String(hookData.cell.raw || "");
                    const icon = getIconFromCache(label, iconCache);

                    if (icon) {
                        const x = hookData.cell.x + 2;
                        const y = hookData.cell.y + 2;
                        doc.addImage(icon, "PNG", x, y, 5, 5);
                    }
                }
            }
        });

        currentY = doc.lastAutoTable.finalY + 7;
    } else {
        currentY += 10;
    }

    /* ------------ Mechanical & Fluid Checks ------------ */

    const formatCheckValue = (val) => {
        if (!val || val === '') return null;
        const valStr = String(val).toLowerCase();

        // Map values to user-friendly text (NO EMOJIS - they don't render in PDFs)
        const valueMap = {
            'none': 'No Leakage',
            'minor': 'Minor Seepage',
            'severe': 'Severe Leak',
            'good': 'Good',
            'low': 'Low',
            'empty': 'Empty',
            'weak': 'Weak',
            'replace': 'Needs Replacement',
            'rust': 'Surface Rust',
            'damaged': 'Damaged/Corroded',
            // Oil Colors
            'amber': 'Amber/Light Brown (Good)',
            'dark-brown': 'Dark Brown (Due for Change)',
            'black': 'Black (Needs Immediate Change)',
            // Coolant Colors
            'green': 'Green (Good)',
            'blue': 'Blue (Good)',
            'pink': 'Pink/Red (Good)',
            'orange': 'Orange (Good)',
            'rusty': 'Rusty/Brown (Contaminated)',
            // Shared Bad Options
            'dirty': 'Dirty/Cloudy',
            'faded': 'Faded/Discolored',
            'unknown': 'Unknown/Other'
        };

        return valueMap[valStr] || val;
    };

    const mechanicalFields = [
        { label: 'Oil Leakage', value: formatCheckValue(data.oilLeakage) },
        { label: 'Oil Color', value: formatCheckValue(data.oilColor) },
        { label: 'Oil Level', value: data.oilLevel != null ? `${data.oilLevel}%` : null },
        { label: 'Coolant Level', value: formatCheckValue(data.coolantLevel) },
        { label: 'Coolant Color', value: formatCheckValue(data.coolantColor) },
        { label: 'Coolant Level %', value: data.coolantLevelPercent != null ? `${data.coolantLevelPercent}%` : null },
        { label: 'Brake Fluid Level', value: formatCheckValue(data.brakeFluidLevel) },
        { label: 'Warning Lights/Messages', value: data.warningLights },
        { label: 'Battery Condition', value: formatCheckValue(data.batteryCondition) },
        { label: 'Exhaust Condition', value: formatCheckValue(data.exhaustCondition) },
        ...(data.mechanicalNotes ? [{ label: 'Mechanical Notes', value: data.mechanicalNotes }] : [])
    ];

    const validMechanical = mechanicalFields.filter(
        f => f.value && String(f.value).trim() !== '' && String(f.value).trim() !== 'N/A'
    );

    if (validMechanical.length > 0) {
        const pageHeight = doc.internal.pageSize.getHeight();
        if (currentY + 60 > pageHeight - 20) {
            doc.addPage();
            currentY = 25;
        }

        drawSectionBar(doc, 'Mechanical & Fluid Checks', currentY, [0, 172, 193]);
        currentY += 15;

        const mechanicalBody = [];
        for (let i = 0; i < validMechanical.length; i += 2) {
            const left = validMechanical[i];
            const right = validMechanical[i + 1];
            mechanicalBody.push([
                left.label,
                left.value,
                right ? right.label : '',
                right ? right.value : ''
            ]);
        }

        autoTable(doc, {
            startY: currentY,
            body: mechanicalBody,
            styles: { fontSize: 10, cellPadding: 2.5 },
            alternateRowStyles: { fillColor: [249, 250, 251] },
            margin: { left: 5, right: 5 },
            columnStyles: {
                0: { fontStyle: 'bold', textColor: [60, 60, 60] },
                1: { textColor: [0, 0, 0] },
                2: { fontStyle: 'bold', textColor: [60, 60, 60] },
                3: { textColor: [0, 0, 0] }
            },
            didParseCell: (hookData) => {
                if (hookData.section === 'body' && (hookData.column.index === 0 || hookData.column.index === 2)) {
                    let pad = hookData.cell.styles.cellPadding;
                    if (typeof pad === 'number') {
                        hookData.cell.styles.cellPadding = { top: pad, right: pad, bottom: pad, left: pad + 6 };
                    } else {
                        hookData.cell.styles.cellPadding = { ...pad, left: (pad.left || 2) + 6 };
                    }
                }
            },
            didDrawCell: (hookData) => {
                if (hookData.section === 'body' && (hookData.column.index === 0 || hookData.column.index === 2)) {
                    const label = String(hookData.cell.raw || '');
                    const icon = getIconFromCache(label, iconCache);

                    if (icon) {
                        const x = hookData.cell.x + 2;
                        const y = hookData.cell.y + 2;
                        doc.addImage(icon, 'PNG', x, y, 5, 5);
                    }
                }
            }
        });

        currentY = doc.lastAutoTable.finalY + 7;
    }



    addFooterAllPages(doc, data);

    if (mode === "preview") {
        const pdfBlob = doc.output("blob");
        return URL.createObjectURL(pdfBlob);
    } else if (mode === "blob") {
        return doc.output("blob");
    } else {
        doc.save(`${data.reg}_report.pdf`);
        return null;
    }
};

/* ----------------- TASK PDF ----------------- */

export const generateTaskPDF = async (task) => {
    const doc = new jsPDF();

    try {
        const logoDataUrl = await toDataURL(businessLogo);
        doc.addImage(logoDataUrl, "PNG", 10, 10, 50, 12);
    } catch (e) {
        console.error("Logo load failed:", e);
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(0, 51, 102);
    doc.text("Job Completion Report", 75, 18);

    doc.setDrawColor(180);
    doc.setLineWidth(0.5);
    doc.line(5, 24, 205, 24);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text(`Job: ${task.title}`, 10, 35);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Customer: ${task.customerName || "N/A"}`, 10, 42);
    doc.text(`Date: ${new Date().toLocaleDateString("en-GB")}`, 10, 47);

    doc.text(`Status: ${task.status}`, 150, 35);
    doc.text(`Approval: ${task.approvalStatus || "Pending"}`, 150, 42);

    doc.line(5, 52, 205, 52);

    autoTable(doc, {
        startY: 55,
        head: [["Field", "Value"]],
        body: [
            ["Assigned To", `${task.assignedTo?.firstName} ${task.assignedTo?.lastName}`],
            ["Assigned By", task.assignedBy?.username],
            ["Deadline", task.deadline ? new Date(task.deadline).toLocaleDateString("en-GB") : "N/A"],
            ["Completion Date", task.updatedAt ? new Date(task.updatedAt).toLocaleDateString("en-GB") : "N/A"]
        ],
        theme: "striped",
        headStyles: { fillColor: [0, 51, 102] },
        styles: { fontSize: 10 }
    });

    let y = doc.lastAutoTable.finalY + 10;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(0, 51, 102);
    doc.text("Description / Notes", 10, y);
    y += 5;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0);
    const descLines = doc.splitTextToSize(task.description || "No description provided.", 190);
    doc.text(descLines, 10, y);
    y += descLines.length * 5 + 10;

    if (task.adminFeedback) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(0, 51, 102);
        doc.text("Admin Feedback", 10, y);
        y += 5;

        doc.setFont("helvetica", "italic");
        doc.setFontSize(10);
        doc.setTextColor(50);
        const feedbackLines = doc.splitTextToSize(task.adminFeedback, 190);
        doc.text(feedbackLines, 10, y);
    }

    const blob = doc.output("blob");
    return URL.createObjectURL(blob);
};
